import { useEffect, useState, type RefObject } from "react";
import type { ScrollOrientation } from "./useScrollBoundary";

const MOVE_THRESHOLD = 6; // px before a press counts as a drag rather than a click

/**
 * Mouse click-drag panning for a scrollable element along one axis — writes
 * `scrollLeft`/`scrollTop` directly while a real drag is in progress; touch
 * stays native. Returns whether a drag is currently in progress, for driving
 * cursor/scroll-behavior styling (see `ScrollArea.Viewport`'s `dragToScroll`
 * prop).
 */
export const useDragToScroll = (
  ref: RefObject<HTMLElement | null>,
  enabled: boolean,
  orientation: ScrollOrientation = "horizontal",
) => {
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;

    const isHorizontal = orientation === "horizontal";
    let startPos = 0;
    let startScroll = 0;
    let pointerId: number | null = null;
    let moved = false;
    let pressing = false; // button held until a real release

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 || e.pointerType !== "mouse") return; // leave touch native
      startPos = isHorizontal ? e.clientX : e.clientY;
      startScroll = isHorizontal ? el.scrollLeft : el.scrollTop;
      pointerId = e.pointerId;
      moved = false;
      pressing = true;
    };
    const onMove = (e: PointerEvent) => {
      if (pointerId === null || e.pointerId !== pointerId) return;
      const pos = isHorizontal ? e.clientX : e.clientY;
      const delta = pos - startPos;
      if (!moved) {
        if (Math.abs(delta) <= MOVE_THRESHOLD) return; // not a drag yet
        moved = true;
        setIsDragging(true);
        el.setPointerCapture(pointerId);
      }
      if (isHorizontal) el.scrollLeft = startScroll - delta;
      else el.scrollTop = startScroll - delta;
    };
    // pointerup / cancel / lost-capture — end the pan
    const endDrag = (e: PointerEvent) => {
      if (e.type !== "pointermove") pressing = false;
      if (pointerId === null || e.pointerId !== pointerId) return;
      pointerId = null;
      if (!moved) return; // a normal click - leave it alone
      setIsDragging(false);
    };
    // swallow the click that would fire on a card after drag
    const onClick = (e: MouseEvent) => {
      if (!moved) return;
      e.preventDefault();
      e.stopPropagation();
      moved = false;
    };
    // stop the browser natively dragging an <img>/<a> inside a card
    const onDragStart = (e: DragEvent) => e.preventDefault();
    // suppress text-selection
    const onSelectStart = (e: Event) => {
      if (pressing) e.preventDefault();
    };

    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", endDrag);
    el.addEventListener("pointercancel", endDrag);
    el.addEventListener("lostpointercapture", endDrag);
    el.addEventListener("click", onClick, true);
    el.addEventListener("dragstart", onDragStart);
    el.addEventListener("selectstart", onSelectStart);

    return () => {
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", endDrag);
      el.removeEventListener("pointercancel", endDrag);
      el.removeEventListener("lostpointercapture", endDrag);
      el.removeEventListener("click", onClick, true);
      el.removeEventListener("dragstart", onDragStart);
      el.removeEventListener("selectstart", onSelectStart);
    };
  }, [enabled, ref, orientation]);

  return isDragging;
};
